/**
 * xr.js — WebXR session management.
 *
 * Owns feature detection, the ENTER VR button, the session lifecycle, and the
 * controller Object3Ds. Everything downstream (input, teleport, avatar) reads
 * from here rather than touching `renderer.xr` directly.
 *
 * Desktop is a first-class path, not a degraded one: with no `navigator.xr` the
 * button reports why and the game runs with mouse-and-keyboard input.
 *
 * @module core/xr
 */

import * as THREE from 'three';
import { EVENTS } from '../constants.js';
import { bus } from './events.js';
import { options } from './testmode.js';

/** Reference space type — 'local-floor' puts y=0 at the real floor. */
const REFERENCE_SPACE = 'local-floor';

class XRManager {
  constructor() {
    /** @type {THREE.WebGLRenderer} */ this.renderer = null;
    /** @type {XRSession|null} */ this.session = null;
    this.supported = false;
    this.presenting = false;

    /** Target-ray spaces (pointing). Index 0 = left, 1 = right. */
    /** @type {THREE.Group[]} */ this.controllers = [];
    /** Grip spaces (where a held object sits). */
    /** @type {THREE.Group[]} */ this.grips = [];
    /** Maps 'left'/'right' to controller index once sources are known. */
    this.handIndex = { left: 0, right: 1 };
    /** @type {Record<string, XRInputSource|null>} */
    this.sources = { left: null, right: null };

    this._button = null;
  }

  /**
   * Wire up XR against a renderer. Safe to call with no WebXR present.
   * @param {THREE.WebGLRenderer} renderer
   * @param {THREE.Object3D} rig The camera rig controllers attach to.
   * @returns {Promise<boolean>} Whether immersive-vr is supported.
   */
  async init(renderer, rig) {
    this.renderer = renderer;

    for (let i = 0; i < 2; i++) {
      const controller = renderer.xr.getController(i);
      controller.name = `controller-${i}`;
      controller.add(makeRayLine());
      rig.add(controller);
      this.controllers.push(controller);

      const grip = renderer.xr.getControllerGrip(i);
      grip.name = `grip-${i}`;
      rig.add(grip);
      this.grips.push(grip);

      controller.addEventListener('connected', (e) => this._onConnected(i, e.data));
      controller.addEventListener('disconnected', () => this._onDisconnected(i));
    }

    renderer.xr.addEventListener('sessionstart', () => this._onSessionStart());
    renderer.xr.addEventListener('sessionend', () => this._onSessionEnd());

    this.supported = await this._checkSupport();
    bus.emit(EVENTS.XR_SUPPORTED, { supported: this.supported });
    return this.supported;
  }

  /** @private @returns {Promise<boolean>} */
  async _checkSupport() {
    if (!options.vr) return false;
    if (typeof navigator === 'undefined' || !navigator.xr) return false;
    try {
      return await navigator.xr.isSessionSupported('immersive-vr');
    } catch {
      return false;
    }
  }

  /** @returns {boolean} */
  isSupported() {
    return this.supported;
  }

  /** @returns {boolean} */
  isPresenting() {
    return this.presenting;
  }

  /**
   * Build the ENTER VR button into a container. Renders a disabled,
   * self-explanatory button when XR is unavailable rather than hiding.
   * @param {HTMLElement} container
   */
  createButton(container) {
    if (!container) return null;
    const btn = document.createElement('button');
    this._button = btn;

    if (!this.supported) {
      btn.textContent = navigator.xr ? 'VR HEADSET NOT FOUND' : 'DESKTOP MODE';
      btn.disabled = true;
      btn.title = 'Play with mouse and keyboard. Open on a Quest browser for VR.';
      container.appendChild(btn);
      return btn;
    }

    btn.textContent = 'ENTER VR';
    btn.addEventListener('click', () => {
      if (this.presenting) this.end();
      else this.request();
    });
    container.appendChild(btn);
    return btn;
  }

  /**
   * Request an immersive-vr session.
   * @returns {Promise<XRSession|null>}
   */
  async request() {
    if (!this.supported || this.presenting) return null;
    try {
      const session = await navigator.xr.requestSession('immersive-vr', {
        optionalFeatures: ['local-floor', 'bounded-floor', 'hand-tracking', 'layers'],
      });
      this.renderer.xr.setReferenceSpaceType(REFERENCE_SPACE);
      await this.renderer.xr.setSession(session);
      return session;
    } catch (err) {
      console.warn('[xr] session request failed:', err);
      bus.emit(EVENTS.XR_SESSION_END, { error: String(err) });
      return null;
    }
  }

  /** End the active session. */
  async end() {
    if (this.session) await this.session.end().catch(() => {});
  }

  /** @private */
  _onSessionStart() {
    this.session = this.renderer.xr.getSession();
    this.presenting = true;
    if (this._button) this._button.textContent = 'EXIT VR';
    if (this.session) {
      this.session.addEventListener('inputsourceschange', (e) => this._onSourcesChange(e));
      for (const src of this.session.inputSources) this._bindSource(src);
    }
    bus.emit(EVENTS.XR_SESSION_START, { session: this.session });
  }

  /** @private */
  _onSessionEnd() {
    this.session = null;
    this.presenting = false;
    if (this._button) this._button.textContent = 'ENTER VR';
    this.sources.left = null;
    this.sources.right = null;
    bus.emit(EVENTS.XR_SESSION_END, {});
  }

  /** @private */
  _onSourcesChange(event) {
    for (const src of event.added) this._bindSource(src);
    for (const src of event.removed) {
      if (this.sources[src.handedness] === src) this.sources[src.handedness] = null;
    }
  }

  /**
   * Record which controller index corresponds to which hand. Three.js assigns
   * indices by connection order, which is not guaranteed to be left-then-right.
   * @private
   */
  _bindSource(src) {
    if (!src || !src.handedness) return;
    if (src.handedness === 'left' || src.handedness === 'right') {
      this.sources[src.handedness] = src;
    }
  }

  /** @private */
  _onConnected(index, data) {
    if (!data || !data.handedness) return;
    if (data.handedness === 'left' || data.handedness === 'right') {
      this.handIndex[data.handedness] = index;
      this.sources[data.handedness] = data;
    }
  }

  /** @private */
  _onDisconnected(index) {
    for (const side of ['left', 'right']) {
      if (this.handIndex[side] === index) this.sources[side] = null;
    }
  }

  /**
   * @param {'left'|'right'} side
   * @returns {THREE.Group|null} Target-ray space.
   */
  getController(side) {
    return this.controllers[this.handIndex[side]] || null;
  }

  /**
   * @param {'left'|'right'} side
   * @returns {THREE.Group|null} Grip space — where a held object should sit.
   */
  getGrip(side) {
    return this.grips[this.handIndex[side]] || null;
  }

  /**
   * @param {'left'|'right'} side
   * @returns {Gamepad|null} The controller's gamepad (buttons, axes, haptics).
   */
  getGamepad(side) {
    const src = this.sources[side];
    return src && src.gamepad ? src.gamepad : null;
  }
}

/** Thin pointing ray shown on each controller. */
function makeRayLine() {
  const geo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, 0, -3),
  ]);
  const mat = new THREE.LineBasicMaterial({
    color: 0xffd166, transparent: true, opacity: 0.45,
  });
  const line = new THREE.Line(geo, mat);
  line.name = 'ray';
  return line;
}

/** The one XR manager. */
export const xr = new XRManager();
